import { create } from "zustand";
import { API_V2 } from "@Models/GWDomain";
import axiosInstance from "../services/axiosInstance";

interface LocalInfo {
  id: number;
  name: string;
  isCentral?: boolean;
}

interface MultiSedeState {
  isCorporate: boolean;
  isCentralUser: boolean;
  accessibleLocals: LocalInfo[];
  selectedLocalId: number | null;
  selectedLocalName: string;
  isLoading: boolean;

  initialize: () => void;
  switchLocal: (localId: number) => Promise<boolean>;
}

export const useMultiSedeStore = create<MultiSedeState>((set, get) => ({
  isCorporate: false,
  isCentralUser: false,
  accessibleLocals: [],
  selectedLocalId: null,
  selectedLocalName: "",
  isLoading: false,

  initialize: () => {
    try {
      const token = localStorage.getItem("access_token");
      if (!token) return;

      const payload = JSON.parse(atob(token.split(".")[1]));
      const isCorporate = payload.isCorporate === true || payload.isCorporate === "true";
      const isCentralUser = payload.isCentralUser === true || payload.isCentralUser === "true";

      let accessibleLocalIds: number[] = [];
      try {
        accessibleLocalIds = typeof payload.accessibleLocalIds === "string"
          ? JSON.parse(payload.accessibleLocalIds)
          : payload.accessibleLocalIds || [];
      } catch { /* ignore */ }

      const selectedLocal = JSON.parse(localStorage.getItem("selectedLocal") || "{}");

      // Build accessible locals list from business relations if available
      const accountInfo = JSON.parse(localStorage.getItem("account_info") || "{}");
      const accessibleLocals: LocalInfo[] = [];

      // Try to get local names from the saved business relations
      const savedRelations = localStorage.getItem("accessible_locals");
      if (savedRelations) {
        try {
          const locals = JSON.parse(savedRelations);
          accessibleLocals.push(...locals);
        } catch { /* ignore */ }
      }

      // If no saved locals, use IDs only
      if (accessibleLocals.length === 0 && accessibleLocalIds.length > 0) {
        for (const id of accessibleLocalIds) {
          accessibleLocals.push({ id, name: `Local ${id}` });
        }
      }

      const currentLocalId = selectedLocal?.id || payload.idlocal;
      const currentLocalName =
        selectedLocal?.nombre ||
        accountInfo?.lounge?.name ||
        accessibleLocals.find((l) => l.id === currentLocalId)?.name ||
        "";

      set({
        isCorporate,
        isCentralUser,
        accessibleLocals,
        selectedLocalId: currentLocalId,
        selectedLocalName: currentLocalName,
      });
    } catch { /* ignore */ }
  },

  switchLocal: async (localId: number) => {
    set({ isLoading: true });
    try {
      const response = await axiosInstance.post(`${API_V2}/auth/switch-local`, { localId });
      const { token, refreshToken } = response.data.data;

      // Persistir el token nuevo. Verificamos read-after-write para detectar
      // si el storage rechazó el setItem (cuota llena, modo privado).
      localStorage.setItem("access_token", token);
      if (localStorage.getItem("access_token") !== token) {
        // Retry una vez. Si vuelve a fallar, abortamos y devolvemos false
        // para que el caller no haga reload con el token viejo.
        localStorage.setItem("access_token", token);
        if (localStorage.getItem("access_token") !== token) {
          set({ isLoading: false });
          return false;
        }
      }
      if (refreshToken) localStorage.setItem("refresh_token", refreshToken);

      // Decode new token to get updated claims
      const payload = JSON.parse(atob(token.split(".")[1]));
      const newIsCentralUser = payload.isCentralUser === true || payload.isCentralUser === "true";

      // Update selected local info
      const locals = get().accessibleLocals;
      const local = locals.find((l) => l.id === localId);
      localStorage.setItem("selectedLocal", JSON.stringify({ id: localId, nombre: local?.name || "" }));

      // No hacer set() aquí — el reload reinicializa todo.
      // Si hacemos set con isLoading:false, React re-renderiza y causa un flash visual.

      return true;
    } catch {
      set({ isLoading: false });
      return false;
    }
  },
}));
